import { h, formatDateTime } from '../ui/dom.js';
import {
  card,
  button,
  field,
  input,
  table,
  emptyState,
  badge,
  metric,
} from '../ui/components.js';
import { api } from '../api.js';
import { getPermissions } from '../store.js';
import type { Page, PageContext } from '../page.js';
import type { AuditLog } from '../types.js';

const ACTION_LABELS: Record<string, string> = {
  LOGIN: 'Login',
  LOGIN_FAILED: 'Login recusado',
  USER_CREATE: 'Usuário criado',
  USER_UPDATE: 'Usuário atualizado',
  USER_DELETE: 'Usuário excluído',
  USER_ROLE_CHANGE: 'Cargo alterado',
  USER_STATUS_CHANGE: 'Status alterado',
  USER_PASSWORD_RESET: 'Credenciais redefinidas',
  TEMPLATE_CREATE: 'Modelo criado',
  TEMPLATE_UPDATE: 'Modelo atualizado',
  TEMPLATE_DELETE: 'Modelo excluído',
  EDITION_CREATE: 'Edição criada',
  EDITION_UPDATE: 'Edição atualizada',
  EDITION_DELETE: 'Edição excluída',
  SCHEDULE_UPDATE: 'Escala alterada',
  MINISTRATION_UPDATE: 'Ministração atualizada',
  READINESS_CHANGE: 'Prontidão alterada',
  MEDIA_UPLOAD: 'Mídia anexada',
  MEDIA_DELETE: 'Mídia removida',
  MEDIA_INCORPORATE_OFFICIAL: 'Incorporado ao modelo oficial',
};

function actionTone(action: string): 'neutral' | 'success' | 'info' | 'warning' | 'danger' {
  if (action.includes('DELETE')) return 'danger';
  if (action === 'LOGIN_FAILED') return 'warning';
  if (action.startsWith('LOGIN')) return 'info';
  if (action.includes('CREATE') || action.includes('UPLOAD')) return 'success';
  return 'neutral';
}

export async function auditPage(context: PageContext): Promise<Page> {
  if (getPermissions()?.visibility !== 'global') {
    return {
      title: 'Trilha de Auditoria',
      body: card({ body: emptyState('Seu perfil não tem acesso à trilha de auditoria.', '◷') }),
    };
  }

  const actionFilter = input({ placeholder: 'ex.: EDITION_CREATE' });
  const entityFilter = input({ placeholder: 'ex.: EDITION, USER, MEDIA' });
  const limitSelect = selectOf(['25', '50', '100', '200']);

  let offset = 0;

  const resultContainer = h('div', { class: 'stack' });
  const summaryContainer = h('div', { class: 'grid grid--4' });

  const load = async (): Promise<void> => {
    resultContainer.replaceChildren(h('div', { class: 'skeleton', style: { width: '50%' } }));

    const response = await api.get<{ items: AuditLog[]; total: number }>('/api/audit', {
      limit: limitSelect.value,
      offset,
      ...(actionFilter.value.trim() ? { action: actionFilter.value.trim().toUpperCase() } : {}),
      ...(entityFilter.value.trim() ? { entityType: entityFilter.value.trim().toUpperCase() } : {}),
    });

    const rows = response.items.map((entry) => [
      h('div', { class: 'mono', text: formatDateTime(entry.createdAt) }),
      h(
        'div',
        { class: 'stack stack--sm' },
        h('div', { style: { fontWeight: '600' }, text: entry.actorName ?? 'Sistema' }),
        h('div', { class: 'faint small', text: entry.actorId ? `ID ${entry.actorId.slice(0, 8)}` : '—' }),
      ),
      h('div', null, badge(ACTION_LABELS[entry.action] ?? entry.action, actionTone(entry.action))),
      h('div', { class: 'mono small', text: `${entry.entityType}${entry.entityId ? ` · ${entry.entityId.slice(0, 8)}` : ''}` }),
      h('div', { text: entry.summary }),
    ]);

    resultContainer.replaceChildren(
      rows.length > 0
        ? table(['Data e hora', 'Autor', 'Ação', 'Entidade', 'Descrição'], rows)
        : emptyState('Nenhum registro encontrado com os filtros atuais.', '◷'),
    );

    summaryContainer.replaceChildren(
      metric('Registros encontrados', response.total, 'accent'),
      metric('Exibindo', response.items.length, 'info'),
      metric('Página', Math.floor(offset / Number(limitSelect.value)) + 1),
      metric('Fuso', '24h UTC', 'default'),
    );
  };

  await load();

  const reload = (): void => {
    void load();
  };

  return {
    title: 'Trilha de Auditoria',
    subtitle: 'Todas as criações, edições e alterações de escala com data, horário e autor.',
    actions: [
      button('Aplicar filtros', { size: 'sm', onClick: reload }),
      button('Limpar', {
        variant: 'ghost',
        size: 'sm',
        onClick: () => {
          actionFilter.value = '';
          entityFilter.value = '';
          offset = 0;
          reload();
        },
      }),
    ],
    body: [
      summaryContainer,
      card({
        title: 'Filtros',
        body: h(
          'div',
          { class: 'form-grid' },
          field({ label: 'Ação', control: actionFilter, hint: 'Código exato, ex.: READINESS_CHANGE' }),
          field({ label: 'Tipo de entidade', control: entityFilter, hint: 'USER, EDITION, MEDIA…' }),
          field({ label: 'Registros por página', control: limitSelect }),
        ),
      }),
      card({ title: 'Histórico', body: resultContainer }),
      h(
        'div',
        { class: 'row row--between' },
        button('← Anterior', {
          variant: 'ghost',
          size: 'sm',
          disabled: offset === 0,
          onClick: () => {
            offset = Math.max(0, offset - Number(limitSelect.value));
            reload();
          },
        }),
        button('Próxima →', {
          variant: 'ghost',
          size: 'sm',
          onClick: () => {
            offset += Number(limitSelect.value);
            reload();
          },
        }),
      ),
    ],
  };
}

function selectOf(values: string[]): HTMLSelectElement {
  const element = h('select', { class: 'select' }) as HTMLSelectElement;
  for (const value of values) {
    element.append(h('option', { value, text: value }));
  }
  element.value = '50';
  return element;
}
